import * as $ from "jquery";
import * as _ from "lodash";
import { BaseModifyModalView, BaseModifyModalViewOptions } from "./_BaseModifyModal";
import { ModuleCollection } from "../../collections/Modules";
import { GenericConfirmModal } from "./GenericConfirmModal";
import { Utils } from "../../common/utils";

// @ts-ignore - webpack require
var template = require("templates/modals/copyModbusConfig.html");
var modalId = "copyModbusConfigModal";

interface CopyModbusConfigModalOptions extends BaseModifyModalViewOptions {
    targetSensorId: number;
    targetSensorName: string;
    onSuccess?: () => void;
}

export class CopyModbusConfigModal extends BaseModifyModalView {
    declare public options: CopyModbusConfigModalOptions;
    private template = _.template(template);
    private moduleCollections: { [key: number]: ModuleCollection } = {};
    private singleOrgId: number | null = null;

    constructor(options: CopyModbusConfigModalOptions) {
        super(
            <any>_.extend({}, options, {
                tagName: "div",
                events: {
                    "shown.bs.modal #copyModbusConfigModal": "handleModalShown",
                    "hidden.bs.modal #copyModbusConfigModal": "handleModalHidden",
                    "change .copy-modbus-config-org-select": "handleOrgChange",
                    "change .copy-modbus-config-install-select": "handleInstallChange",
                    "change .copy-modbus-config-module-select": "handleModuleChange",
                    "change .copy-modbus-config-sensor-select": "handleSensorChange",
                    "click .copy-modbus-config-submit": "handleSubmitClick",
                },
            })
        );

        this.modalIdSelector = modalId;
        this.moduleCollections = {};
        
        // Check if user only belongs to one organization
        if (this.options.organizationCollection && this.options.organizationCollection.length === 1) {
            this.singleOrgId = this.options.organizationCollection.at(0).get('id');
        }
    }

    render() {
        this.$el.html(
            this.template({
                elementId: modalId,
                organizationCollection: this.options.organizationCollection,
                installCollection: this.options.installCollection,
                singleOrgId: this.singleOrgId,
            })
        );
        return this;
    }

    show() {
        (<any>this.$("#" + modalId)).modal("show");
        this.resetForm();
    }

    private resetForm() {
        if (this.singleOrgId) {
            // Pre-select the single organization
            this.$(".copy-modbus-config-org-select").val(String(this.singleOrgId)).trigger("change");
        } else {
            this.$(".copy-modbus-config-org-select").val("").trigger("change");
        }
        this.$(".copy-modbus-config-install-select").val("").trigger("change");
        this.$(".copy-modbus-config-module-select").empty().append('<option></option>');
        this.$(".copy-modbus-config-sensor-select").empty().append('<option></option>');
        this.updateSubmitButtonState();
    }

    handleModalShown() {
        // Initialize selects after modal is shown so Select2 can calculate dimensions correctly
        // Use dropdownParent to ensure dropdown is properly attached to modal
        const modalElement = this.$("#" + modalId);
        this.$('select').select2(_.extend({
            dropdownParent: modalElement
        }, Utils.Select2.defaultOptions));
        
        // Disable install selector if no org is selected (unless single org is preselected)
        if (!this.singleOrgId) {
            this.$(".copy-modbus-config-install-select").select2(_.extend({
                dropdownParent: modalElement,
                disabled: true
            }, Utils.Select2.defaultOptions));
        }
        
        // Disable module and sensor selectors initially
        this.$(".copy-modbus-config-module-select").select2(_.extend({
            dropdownParent: modalElement,
            disabled: true
        }, Utils.Select2.defaultOptions));
        
        this.$(".copy-modbus-config-sensor-select").select2(_.extend({
            dropdownParent: modalElement,
            disabled: true
        }, Utils.Select2.defaultOptions));
        
        // Hide organization selector if user only belongs to one organization
        if (this.singleOrgId) {
            this.$(".copy-modbus-config-org-select").closest(".form-group").hide();
            // Pre-select the single organization and trigger change to populate installations
            this.$(".copy-modbus-config-org-select").val(String(this.singleOrgId)).trigger("change");
        }
    }

    handleModalHidden() {
        // Don't remove from DOM - let the parent view manage it
    }

    handleOrgChange() {
        const orgId = Number(this.$(".copy-modbus-config-org-select").val());
        const installSelect = this.$(".copy-modbus-config-install-select");
        const moduleSelect = this.$(".copy-modbus-config-module-select");
        const sensorSelect = this.$(".copy-modbus-config-sensor-select");
        const modalElement = this.$("#" + modalId);

        installSelect.empty();
        moduleSelect.empty();
        sensorSelect.empty();

        if (!orgId) {
            installSelect.append('<option></option>');
            installSelect.select2(_.extend({
                dropdownParent: modalElement,
                disabled: true
            }, Utils.Select2.defaultOptions));
            this.updateSubmitButtonState();
            return;
        }

        // Filter installations by organization
        const orgInstalls = this.options.installCollection.where({
            organization_id: orgId,
        });

        const installOptions = [{ id: "", text: "" }].concat(
            orgInstalls.map((install) => ({
                id: install.get("id"),
                text: install.getDisplayName(),
            }))
        );

        installSelect.select2(_.extend({
            dropdownParent: modalElement,
            disabled: false,
            data: installOptions,
        }, Utils.Select2.defaultOptions));

        this.updateSubmitButtonState();
    }

    handleInstallChange() {
        const installId = Number(this.$(".copy-modbus-config-install-select").val());
        const moduleSelect = this.$(".copy-modbus-config-module-select");
        const sensorSelect = this.$(".copy-modbus-config-sensor-select");
        const modalElement = this.$("#" + modalId);

        moduleSelect.empty();
        sensorSelect.empty();

        if (!installId) {
            moduleSelect.append('<option></option>');
            sensorSelect.append('<option></option>');
            moduleSelect.select2(_.extend({
                dropdownParent: modalElement,
                disabled: true
            }, Utils.Select2.defaultOptions));
            sensorSelect.select2(_.extend({
                dropdownParent: modalElement,
                disabled: true
            }, Utils.Select2.defaultOptions));
            this.updateSubmitButtonState();
            return;
        }

        if (this.options.permissionCollection.canViewInstall(Number(installId))) {
            if (!_.has(this.moduleCollections, installId)) {
                this.moduleCollections[installId] = new ModuleCollection([], { installId: installId });
                this.moduleCollections[installId].fetch();
            }

            if (this.moduleCollections[installId].isFetching()) {
                // Show loading state while fetching
                moduleSelect.append('<option value="">Loading...</option>');
                moduleSelect.select2(_.extend({
                    dropdownParent: modalElement,
                    disabled: true,
                }, Utils.Select2.defaultOptions));
                
                // Ensure sensor selector is disabled while modules are loading
                sensorSelect.append('<option></option>');
                sensorSelect.select2(_.extend({
                    dropdownParent: modalElement,
                    disabled: true,
                }, Utils.Select2.defaultOptions));

                this.moduleCollections[installId].once('sync', () => {
                    const moduleOptions = this.buildModuleOptions(installId);
                    moduleSelect.empty().select2(_.extend({
                        dropdownParent: modalElement,
                        disabled: false,
                        data: moduleOptions,
                    }, Utils.Select2.defaultOptions));
                    moduleSelect.trigger('change');
                    this.updateSubmitButtonState();
                });
            } else {
                // Data already loaded, enable immediately
                const moduleOptions = this.buildModuleOptions(installId);
                moduleSelect.select2(_.extend({
                    dropdownParent: modalElement,
                    disabled: false,
                    data: moduleOptions,
                }, Utils.Select2.defaultOptions));
                moduleSelect.trigger('change');
                this.updateSubmitButtonState();
            }
        } else {
            // No permission, keep disabled
            moduleSelect.append('<option></option>');
            moduleSelect.select2(_.extend({
                dropdownParent: modalElement,
                disabled: true,
            }, Utils.Select2.defaultOptions));
            sensorSelect.append('<option></option>');
            sensorSelect.select2(_.extend({
                dropdownParent: modalElement,
                disabled: true,
            }, Utils.Select2.defaultOptions));
            this.updateSubmitButtonState();
        }
    }

    handleModuleChange() {
        const installId = Number(this.$(".copy-modbus-config-install-select").val());
        const moduleSelect = this.$(".copy-modbus-config-module-select");
        const moduleNumberValue = (<string>moduleSelect.val()) || "";
        const sensorSelect = this.$(".copy-modbus-config-sensor-select");
        const modalElement = this.$("#" + modalId);

        sensorSelect.empty();

        if (!moduleNumberValue || !installId) {
            sensorSelect.append('<option></option>');
            sensorSelect.select2(_.extend({
                dropdownParent: modalElement,
                disabled: true
            }, Utils.Select2.defaultOptions));
            this.updateSubmitButtonState();
            return;
        }

        // Show loading state while processing (matching simple widget pattern)
        sensorSelect.append('<option value="">Loading...</option>');
        sensorSelect.select2(_.extend({
            dropdownParent: modalElement,
            disabled: true,
        }, Utils.Select2.defaultOptions));

        // Process sensor options (similar to simple widget channel processing)
        const sensorOptions: any[] = [{ id: '', text: '' }];
        if (moduleNumberValue) {
            const module = this.moduleCollections[installId].findWhere({ module_number: moduleNumberValue });
            if (module) {
                // Find all Modbus sensors (type 22) on this module
                for (let i = 1; i < 5; i++) {
                    const con = module.getConnector(i);
                    if (con && con["sensor_type"] === 22) {
                        // Exclude the target sensor itself
                        if (con["sensor_id"] !== this.options.targetSensorId) {
                            const sensorName = con["sensor_name"] || `Sensor ${i}`;
                            const displayText = `${sensorName} (Connector: ${i})`;
                            sensorOptions.push({
                                id: String(con["sensor_id"]),
                                text: displayText,
                            });
                        }
                    }
                }
            }
        }

        // Enable with data once processing is complete (using setTimeout to show loading state briefly)
        setTimeout(() => {
            sensorSelect.empty().select2(_.extend({
                dropdownParent: modalElement,
                disabled: !moduleNumberValue.length,
                data: sensorOptions,
            }, Utils.Select2.defaultOptions));
            sensorSelect.trigger('change');
            this.updateSubmitButtonState();
        }, 50); // Brief delay to show loading state, matching UX pattern
    }

    handleSensorChange() {
        this.updateSubmitButtonState();
    }

    private buildModuleOptions(installId: number) {
        const options: any[] = [];
        this.moduleCollections[installId].each((module) => {
            // Check if module has any Modbus sensors (type 22)
            for (let i = 1; i < 5; i++) {
                const con = module.getConnector(i);
                if (con && con["sensor_type"] === 22) {
                    const mod = module.get("module_number");
                    const option = { id: String(mod), text: module.getDisplayName() };
                    options.push(option);
                    break;
                }
            }
        });

        if (options.length) return [{ id: '', text: '' }].concat(options);
        return [{ id: '', text: 'No Modbus modules found' }];
    }

    private updateSubmitButtonState() {
        const orgId = Number(this.$(".copy-modbus-config-org-select").val());
        const installId = Number(this.$(".copy-modbus-config-install-select").val());
        const moduleNumber = Number(this.$(".copy-modbus-config-module-select").val());
        const sensorId = Number(this.$(".copy-modbus-config-sensor-select").val());

        const isValid =
            !!orgId && !!installId && !!moduleNumber && !!sensorId && sensorId !== this.options.targetSensorId;

        this.$(".copy-modbus-config-submit").prop("disabled", !isValid);
    }

    handleSubmitClick() {
        const sourceSensorId = Number(this.$(".copy-modbus-config-sensor-select").val());
        const installId = Number(this.$(".copy-modbus-config-install-select").val());
        const moduleSelect = this.$(".copy-modbus-config-module-select");
        const moduleNumberValue = moduleSelect.val();

        if (!sourceSensorId || sourceSensorId === this.options.targetSensorId) {
            return;
        }

        // Get source sensor name - try multiple ways to find the module
        let module: any = null;
        if (this.moduleCollections[installId]) {
            // Try as string first
            module = this.moduleCollections[installId].findWhere({
                module_number: moduleNumberValue,
            });

            // If not found, try as number
            if (!module && !isNaN(Number(moduleNumberValue))) {
                module = this.moduleCollections[installId].findWhere({
                    module_number: Number(moduleNumberValue),
                });
            }

            // If still not found, try finding by module_id
            if (!module) {
                module = this.moduleCollections[installId].find((m: any) => {
                    return String(m.get('module_number')) === String(moduleNumberValue) ||
                           String(m.get('module_id')) === String(moduleNumberValue);
                });
            }
        }

        let sourceSensorName = "Unknown";
        if (module) {
            for (let i = 1; i < 5; i++) {
                const con = module.getConnector(i);
                if (con && con["sensor_id"] === sourceSensorId) {
                    sourceSensorName = con["sensor_name"] || `Sensor ${i}`;
                    break;
                }
            }
        }
        
        // If still unknown, try to extract from the selected option text as fallback
        if (sourceSensorName === "Unknown") {
            const selectedOption = this.$(".copy-modbus-config-sensor-select option:selected");
            if (selectedOption.length) {
                const optionText = selectedOption.text();
                // Extract sensor name from format: "Sensor Name (ID: 12345, Connector: 2)"
                const match = optionText.match(/^(.+?)\s*\(ID:/);
                if (match && match[1]) {
                    sourceSensorName = match[1].trim();
                }
            }
        }

        // Show confirmation modal
        const confirmModal = new GenericConfirmModal({
            name: "copyModbusConfig",
            titleText: "Confirm Copy Configuration",
            text: `You are about to copy the Modbus bridge configuration from sensor ID ${sourceSensorId} (${sourceSensorName}) to sensor ID ${this.options.targetSensorId} (${this.options.targetSensorName}).`,
            warning: "This will replace all existing configuration on the target sensor. This action cannot be undone.",
            buttonText: "Copy Configuration",
            cancelButtonText: "Cancel",
            func: () => {
                return this.performCopy(sourceSensorId);
            },
        });

        confirmModal.render();
        $("body").append(confirmModal.$el);
        confirmModal.show();
    }

    private performCopy(sourceSensorId: number): JQuery.Promise<any> {
        const deferred = $.Deferred();

        $.ajax({
            type: "POST",
            url: `/api/v1/sensor/${this.options.targetSensorId}/copy_config_from/${sourceSensorId}`,
            dataType: "json",
            contentType: "application/json; charset=utf-8",
            success: (response) => {
                this.hide();
                if (this.options.onSuccess) {
                    this.options.onSuccess();
                }
                (<any>$).notify({
                    title: 'Success',
                    message: 'Sensor configuration copied successfully',
                    icon: 'fa fa-check-circle',
                }, {
                    animate: {
                        enter: 'animated fadeInUp',
                    },
                    type: 'success',
                });
                deferred.resolve(response);
            },
            error: (xhr, status, error) => {
                let errorMessage = "Failed to copy configuration";
                if (xhr.responseJSON && xhr.responseJSON.message) {
                    errorMessage = xhr.responseJSON.message;
                } else if (xhr.statusText) {
                    errorMessage = `${errorMessage}: ${xhr.statusText}`;
                }
                (<any>$).notify({
                    title: 'Error',
                    message: errorMessage,
                    icon: 'fa fa-exclamation-circle',
                }, {
                    animate: {
                        enter: 'animated fadeInUp',
                    },
                    type: 'danger',
                });
                deferred.reject(xhr, status, error);
            },
        });

        return deferred.promise();
    }
}




// WEBPACK FOOTER //
// ./srv/public/js/views/modals/CopyModbusConfigModal.ts